import React, { useState, useEffect, useMemo } from 'react';
import { 
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond,
  Eye, MessageSquare, Camera, FileCheck, Calendar, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const IconMap = {
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond,
  Eye, Camera, FileCheck, Calendar, Users, MessageSquare
};

// Requirement type labels
const requirementLabels = {
  five_star_reviews: { label: '5-Star Reviews', icon: Star },
  profile_views: { label: 'Profile Views', icon: Eye },
  total_reviews: { label: 'Total Reviews', icon: MessageSquare },
  bookings: { label: 'Bookings', icon: Calendar },
  response_rate: { label: 'Response Rate', icon: CheckCircle },
  years_active: { label: 'Years Active', icon: Calendar },
  photos_uploaded: { label: 'Photos Uploaded', icon: Camera },
  certificates_uploaded: { label: 'Certificates Uploaded', icon: FileCheck }
};

// Badge categories configuration
const badgeCategories = {
  popularity: {
    id: 'popularity',
    name: 'Popularity Badges',
    description: 'These badges are earned based on your profile visibility and community engagement. As more clients view your profile and leave reviews, you unlock these recognition badges.',
    icon: Star,
    gradient: 'from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800/50',
    iconColor: 'text-yellow-500',
    requirementTypes: ['profile_views', 'five_star_reviews', 'total_reviews']
  },
  profile: {
    id: 'profile',
    name: 'Profile Badges',
    description: 'Complete your trainer profile to earn these badges. Upload photos and certificates to showcase your professionalism and build trust with potential clients.',
    icon: Camera,
    gradient: 'from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800/50',
    iconColor: 'text-blue-500',
    requirementTypes: ['photos_uploaded', 'certificates_uploaded']
  },
  quality: {
    id: 'quality',
    name: 'Quality Badges',
    description: 'These prestigious badges recognize trainers who consistently deliver excellent service. Earn them by receiving outstanding feedback from your clients.',
    icon: Award,
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
    borderColor: 'border-green-200 dark:border-green-800/50',
    iconColor: 'text-green-500',
    requirementTypes: ['five_star_reviews', 'bookings', 'response_rate', 'years_active']
  }
};

// Boost badge descriptions - detailed explanations for each boost type
const boostBadgeDescriptions = {
  boosted: {
    fullDescription: 'The Boosted Premium badge is a prestigious indicator that sets you apart from other trainers. This premium status significantly increases your visibility across the platform, ensuring you appear at the top of search results and gain more exposure to potential clients.',
    howToEarn: 'Upgrade to a Boosted Premium membership package from your trainer dashboard.',
    benefits: [
      'Priority placement in search results',
      'Featured position on the homepage',
      'Golden badge displayed on your profile card',
      'Increased visibility to potential clients',
      'Stand out from regular listings'
    ]
  }
};

// Helper function to determine badge category
const getBadgeCategory = (achievement) => {
  if (!achievement.requirements || achievement.requirements.length === 0) {
    return 'popularity';
  }
  
  const reqTypes = achievement.requirements.map(r => r.type);
  
  if (reqTypes.includes('photos_uploaded') || reqTypes.includes('certificates_uploaded')) {
    return 'profile';
  }
  
  if (reqTypes.includes('five_star_reviews') && !reqTypes.includes('profile_views')) {
    return 'quality';
  }
  
  if (reqTypes.includes('profile_views') || reqTypes.includes('total_reviews')) {
    return 'popularity';
  }
  
  return 'popularity';
};

const BadgesPage = () => {
  const [achievements, setAchievements] = useState([]);
  const [boostCategories, setBoostCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [achievementsRes, boostRes] = await Promise.all([
        fetch(`${API_BASE}/api/achievements`),
        fetch(`${API_BASE}/api/boost-categories`)
      ]);
      
      const achievementsData = await achievementsRes.json();
      const boostData = await boostRes.json();
      
      setAchievements(achievementsData.achievements || []);
      setBoostCategories(boostData.categories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group achievements by category
  const groupedAchievements = useMemo(() => {
    const grouped = {
      popularity: [],
      profile: [],
      quality: []
    };
    
    achievements.forEach(achievement => {
      const category = getBadgeCategory(achievement);
      grouped[category].push(achievement);
    });
    
    return grouped;
  }, [achievements]);

  // Badge Card Component
  const BadgeCard = ({ achievement }) => {
    const IconComponent = IconMap[achievement.icon] || Trophy;
    
    return (
      <Card 
        className="overflow-hidden hover:shadow-lg transition-all duration-300 border hover:border-opacity-50 dark:bg-gray-800 dark:border-gray-700"
        style={{ borderColor: `${achievement.color}30` }}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div 
              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: `${achievement.color}15`,
                border: `2px solid ${achievement.color}40`
              }}
            >
              <IconComponent 
                className="w-6 h-6" 
                style={{ color: achievement.color }}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="font-bold text-gray-900 dark:text-white">
                  {achievement.name}
                </h4>
                <Badge 
                  className="text-white text-xs font-semibold px-2 py-0.5 inline-flex items-center gap-1"
                  style={{ backgroundColor: achievement.color }}
                >
                  <IconComponent className="w-3 h-3" />
                  <span>{achievement.badge_text || achievement.name}</span>
                </Badge>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {achievement.description}
              </p>
              
              {achievement.requirements && achievement.requirements.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    How to Earn
                  </p>
                  <ul className="space-y-1">
                    {achievement.requirements.map((req, idx) => {
                      const reqInfo = requirementLabels[req.type] || { label: req.type, icon: CheckCircle };
                      const ReqIcon = reqInfo.icon;
                      
                      return (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <ReqIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-200 text-xs">
                            {req.description || `${req.target} ${reqInfo.label}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Boost Badge Card Component
  const BoostBadgeCard = ({ boost }) => {
    const IconComponent = IconMap[boost.icon] || Crown;
    const extraInfo = boostBadgeDescriptions[boost.key] || {};
    
    return (
      <Card 
        className="overflow-hidden hover:shadow-lg transition-all duration-300 border hover:border-opacity-50 dark:bg-gray-800 dark:border-gray-700"
        style={{ borderColor: `${boost.color}30` }}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div 
              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: `${boost.color}15`,
                border: `2px solid ${boost.color}40`
              }}
            >
              <IconComponent 
                className="w-6 h-6" 
                style={{ color: boost.color }}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="font-bold text-gray-900 dark:text-white">
                  {boost.label}
                </h4>
                <Badge 
                  className="text-white text-xs font-semibold px-2 py-0.5 inline-flex items-center gap-1"
                  style={{ backgroundColor: boost.color }}
                >
                  <IconComponent className="w-3 h-3" />
                  <span>{boost.badge_text || boost.label}</span>
                </Badge>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {extraInfo.fullDescription || boost.description}
              </p>
              
              {/* How to Earn */}
              {extraInfo.howToEarn && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 mb-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    How to Get
                  </p>
                  <p className="text-xs text-gray-700 dark:text-gray-200">
                    {extraInfo.howToEarn}
                  </p>
                </div>
              )}
              
              {/* Benefits */}
              {extraInfo.benefits && extraInfo.benefits.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg p-2.5">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1.5">
                    Benefits
                  </p>
                  <ul className="space-y-1">
                    {extraInfo.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Category Section Component
  const CategorySection = ({ category, badges }) => {
    const config = badgeCategories[category];
    const CategoryIcon = config.icon;
    
    if (badges.length === 0) return null;
    
    return (
      <section className="mb-12">
        <div className={`bg-gradient-to-br ${config.gradient} rounded-2xl p-6 border ${config.borderColor} mb-6`}>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <CategoryIcon className={`w-10 h-10 ${config.iconColor}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {config.name}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {config.description}
              </p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {badges.map((achievement) => (
            <BadgeCard key={achievement.key || achievement.achievement_id} achievement={achievement} />
          ))}
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  // Filter active boost categories - exclude 'listing' (Regular Listing) as it's not a meaningful badge
  const activeBoosts = boostCategories.filter(b => b.active && b.key !== 'listing');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <ResponsiveHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-6 shadow-lg">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Achievement Badges
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Discover all the badges that trainers can earn on our platform and learn how to achieve them.
          </p>
        </div>

        {/* How Badges Work */}
        <div className="mb-12 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                How Do Badges Work?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Trainers automatically earn badges when they reach certain milestones on the platform. 
                Earned badges are displayed on the trainer's profile card and profile page. 
                These badges reflect the trainer's experience, professionalism, and achievements.
              </p>
            </div>
          </div>
        </div>

        {/* Listing & Boost Badges Section */}
        {activeBoosts.length > 0 && (
          <section className="mb-12">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800/50 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Crown className="w-10 h-10 text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Listing & Premium Badges
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    These badges indicate your listing status on the platform. Upgrade to premium to boost your visibility and attract more clients with priority placement in search results.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activeBoosts.map((boost) => (
                <BoostBadgeCard key={boost.key || boost.category_id} boost={boost} />
              ))}
            </div>
          </section>
        )}

        {/* No Badges State */}
        {achievements.length === 0 && activeBoosts.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No badges available yet.</p>
          </div>
        ) : (
          <>
            {/* Popularity Badges Section */}
            <CategorySection category="popularity" badges={groupedAchievements.popularity} />
            
            {/* Profile Badges Section */}
            <CategorySection category="profile" badges={groupedAchievements.profile} />
            
            {/* Quality Badges Section */}
            <CategorySection category="quality" badges={groupedAchievements.quality} />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BadgesPage;
